/**
 * Latest subject box from any tool-prep preview that uses the shared bounding box.
 * Open in Editor copies this onto the result so the editor can place the same
 * resized subject. One store, every background-removal tool.
 */
let current = null;
let adjusted = false;

export function markToolPrepSubjectAdjusted(value) {
  adjusted = Boolean(value);
}

export function isToolPrepSubjectAdjusted() {
  return adjusted;
}

/** Replace the stored placement. Null keeps the previous one (preview tear-down). */
export function publishToolPrepSubjectPlacement(placement) {
  if (!placement) return;
  current = {
    ...placement,
    userAdjusted: Boolean(placement.userAdjusted) || adjusted,
  };
}

export function readToolPrepSubjectPlacement() {
  return current;
}

export function clearToolPrepSubjectPlacement() {
  current = null;
  adjusted = false;
}

function normalizeCrop(crop) {
  if (!crop || !(crop.width > 0) || !(crop.height > 0)) return null;
  const imageWidth = Number(crop.imageWidth);
  const imageHeight = Number(crop.imageHeight);
  if (!(imageWidth > 0) || !(imageHeight > 0)) return null;
  return {
    x: Number(crop.x) || 0,
    y: Number(crop.y) || 0,
    width: Number(crop.width),
    height: Number(crop.height),
    imageWidth,
    imageHeight,
  };
}

/** Fractions of the tool-prep stage, plus the source crop inside that frame. */
export function normalizeSubjectPlacement(raw) {
  if (!raw || typeof raw !== "object") return null;
  const width = Number(raw.width);
  const height = Number(raw.height);
  if (!(width > 0) || !(height > 0)) return null;
  return {
    x: Number(raw.x) || 0,
    y: Number(raw.y) || 0,
    width,
    height,
    rotation: Number(raw.rotation) || 0,
    userAdjusted: Boolean(raw.userAdjusted),
    crop: normalizeCrop(raw.crop),
  };
}

/**
 * Attach the live bounding-box placement when the user actually moved or resized it.
 * Baked bitmaps and soft-shadow results keep their own pixels.
 */
export function mergeToolPrepSubjectPlacement(result) {
  if (!result || typeof result !== "object") return result;
  const fromResult = normalizeSubjectPlacement(result.subjectPlacement);
  const placement = fromResult?.userAdjusted
    ? fromResult
    : normalizeSubjectPlacement(current);
  if (!placement?.userAdjusted) return result;
  if (result.effectBaked || result.paddingBaked || result.preserveShadowLayout) {
    return result;
  }
  return { ...result, subjectPlacement: placement };
}

/**
 * How the editor should apply a handoff placement.
 * Resize / padding finish their own canvas first (`defer`), then this frame is applied.
 * Tools that already send rotation (rotate / flip) keep that value.
 */
export function subjectPlacementHandoffPlan(result) {
  const placement = normalizeSubjectPlacement(result?.subjectPlacement);
  if (!placement?.userAdjusted) return null;
  if (result?.effectBaked || result?.paddingBaked || result?.preserveShadowLayout) {
    return null;
  }
  return {
    ...placement,
    defer: Boolean(result?.preset || (result?.padding && !result.paddingBaked)),
    applyRotation: result?.rotation == null,
  };
}
